import moment from "moment";
import { isUnassignedSchedulerEvent } from "./unassignedEvents.utils";

const EVENT_SHIFT_VIEW = {
  DAILY: "3",
  MONTHLY: "2",
  WEEKLY: "4",
};

/**
 * @param {unknown} tags
 * @param {string} search lower-cased search term
 * @returns {boolean}
 */
function eventTagsMatchSearch(tags, search) {
  if (!tags) return false;
  if (Array.isArray(tags)) {
    return tags.some((tag) => String(tag).toLowerCase().includes(search));
  }
  return String(tags).toLowerCase().includes(search);
}

/** Minimum characters before Event Workspace search filters the calendar. */
export const EVENTS_WORKSPACE_SEARCH_MIN_LENGTH = 3;

/**
 * Status / type badge labels shown on event cards (Tag display setting).
 * Matched by prefix so typing "dra" / "eve" works after 3 characters.
 */
const EVENT_WORKSPACE_BADGE_MATCHERS = [
  { label: "draft", matches: (event) => Boolean(event?.isDraft) },
  { label: "published", matches: (event) => !event?.isDraft },
  { label: "shift", matches: (event) => Boolean(event?.isShift) },
  { label: "event", matches: (event) => !event?.isShift },
];

/**
 * @param {string} searchValue
 * @returns {boolean}
 */
export function shouldApplyEventsWorkspaceSearch(searchValue = "") {
  return (
    searchValue.trim().length >= EVENTS_WORKSPACE_SEARCH_MIN_LENGTH
  );
}

/**
 * Event Workspace search: title, assigned tags, and card badges (Draft / Event / Shift / Published).
 * Applies only after {@link EVENTS_WORKSPACE_SEARCH_MIN_LENGTH} characters.
 * @param {Array<object>} events
 * @param {string} searchValue
 * @returns {Array<object>}
 */
export function filterEventsWorkspaceBySearch(events = [], searchValue = "") {
  const search = searchValue?.trim().toLowerCase();
  if (!search || search.length < EVENTS_WORKSPACE_SEARCH_MIN_LENGTH) {
    return events;
  }

  return events.filter((event) => {
    if (event?.title?.toLowerCase().includes(search)) return true;
    if (eventTagsMatchSearch(event?.eventTags, search)) return true;

    return EVENT_WORKSPACE_BADGE_MATCHERS.some(
      ({ label, matches }) => label.startsWith(search) && matches(event),
    );
  });
}

/**
 * Count scheduler event card occurrences (per day / per row), not unique ids.
 * @param {Array<{ id?: number }>} events
 * @returns {number}
 */
export function countSchedulerEventOccurrences(events = []) {
  return (events || []).filter((event) => event?.id != null).length;
}

/**
 * Count unique scheduler events visible in Event Workspace (by id).
 * @param {Array<{ id?: number }>} events
 * @returns {number}
 */
export function countUniqueSchedulerEvents(events = []) {
  const ids = new Set();
  events.forEach((event) => {
    if (event?.id != null) ids.add(event.id);
  });
  return ids.size;
}

/**
 * Draft events/shifts visible in the calendar grid (excludes unassigned rows).
 * Matches monthly `getFilteredListing` and daily/weekly assigned-event rendering.
 * @param {Array<{ id?: number, isDraft?: boolean, isShift?: boolean }>} events
 * @param {string|null} [date] Occurrence date (`YYYY-MM-DD`) for confirmation counts.
 * @returns {Array<{ id: number, isShift: boolean, date?: string }>}
 */
export function collectDraftPublishItemsFromEvents(events = [], date = null) {
  return events
    .filter((ev) => ev?.isDraft && !isUnassignedSchedulerEvent(ev))
    .map((ev) => {
      const item = { id: ev.id, isShift: Boolean(ev.isShift) };
      if (date) item.date = date;
      return item;
    });
}

/**
 * @param {Array<{ date?: string, schedulerEvents?: Array<object> }>} weekData
 * @returns {Array<{ id: number, isShift: boolean, date?: string }>}
 */
export function collectDraftPublishItemsFromWeekData(weekData = []) {
  const draftItems = [];
  weekData.forEach((day) => {
    const date = day?.date
      ? moment(day.date).format("YYYY-MM-DD")
      : null;
    draftItems.push(
      ...collectDraftPublishItemsFromEvents(day?.schedulerEvents || [], date),
    );
  });
  return draftItems;
}

export function dedupeDraftPublishItems(items = []) {
  const byKey = new Map();
  items.forEach((item) => {
    if (!item?.id) return;
    // Keep staff cards distinct for selection matching; modal counts ignore userId.
    const hasUserId = Object.prototype.hasOwnProperty.call(item, "userId");
    const dateKey =
      item.date != null && item.date !== "" ? `_${item.date}` : "";
    const key = hasUserId
      ? `${item.id}${dateKey}_${
          item.userId == null || item.userId === ""
            ? "unassigned"
            : item.userId
        }`
      : `${item.id}${dateKey}`;
    if (byKey.has(key)) return;
    const next = { id: item.id, isShift: Boolean(item.isShift) };
    if (dateKey) {
      next.date = item.date;
    }
    if (hasUserId) {
      next.userId = item.userId;
    }
    if (item.isDraft != null) {
      next.isDraft = Boolean(item.isDraft);
    }
    if (item.isUnassigned != null) {
      next.isUnassigned = Boolean(item.isUnassigned);
    }
    byKey.set(key, next);
  });
  return [...byKey.values()];
}

/**
 * Selected Publish must use the same eligibility as Global Publish:
 * only drafts that are already in the visible/global publishable set.
 * Excludes unassigned-only cards that Global Publish does not publish.
 *
 * @param {Record<string, object>|object[]} selectedEvents
 * @param {Array<{ id: number, isShift?: boolean, userId?: number|null }>} visibleDraftItems
 * @returns {Array<{ id: number, isShift: boolean, userId?: number|null, isDraft?: boolean }>}
 */
export function getSelectedPublishableDraftItems(
  selectedEvents = {},
  visibleDraftItems = [],
) {
  const selectedList = Array.isArray(selectedEvents)
    ? selectedEvents
    : Object.values(selectedEvents || {});

  const selectedDrafts = selectedList.filter(
    (item) => item?.isDraft && item?.id != null && !item?.isUnassigned,
  );
  if (!selectedDrafts.length || !visibleDraftItems?.length) return [];

  const visibleById = new Map();
  const visibleStaffKeys = new Set();
  visibleDraftItems.forEach((item) => {
    if (item?.id == null) return;
    visibleById.set(item.id, item);
    const hasUserId = Object.prototype.hasOwnProperty.call(item, "userId");
    if (hasUserId && item.userId != null && item.userId !== "") {
      visibleStaffKeys.add(`${item.id}_${item.userId}`);
    }
  });

  const matched = selectedDrafts.filter((item) => {
    const hasUserId = Object.prototype.hasOwnProperty.call(item, "userId");
    const hasConcreteUserId =
      hasUserId && item.userId != null && item.userId !== "";

    // Staff-name weekly cards: match the same staff-scoped draft Global uses.
    if (hasConcreteUserId) {
      return visibleStaffKeys.has(`${item.id}_${item.userId}`);
    }

    // Daily / Weekly / Monthly (and null userId): match by event id.
    return visibleById.has(item.id);
  });

  return dedupeDraftPublishItems(matched);
}

/**
 * Confirmation-modal event/shift totals.
 * Counts unique draft occurrences by `id + date` when date is present, otherwise
 * by `id`. Staff-name weekly cards may repeat the same event across users —
 * those are not counted separately (userId is ignored).
 * @param {Array<{ id: number, isShift?: boolean, date?: string, userId?: number|string|null }>} items
 * @returns {{ totalCount: number, shiftCount: number, eventCount: number }}
 */
export function getPublishItemCounts(items = []) {
  const seen = new Set();
  let shiftCount = 0;
  let eventCount = 0;

  (items || []).forEach((item) => {
    if (item?.id == null) return;
    const key =
      item.date != null && item.date !== ""
        ? `${item.id}_${item.date}`
        : String(item.id);
    if (seen.has(key)) return;
    seen.add(key);

    if (item.isShift) {
      shiftCount += 1;
    } else {
      eventCount += 1;
    }
  });

  return {
    totalCount: shiftCount + eventCount,
    shiftCount,
    eventCount,
  };
}

/**
 * @param {string} calendarViewId
 * @param {Date|string} date
 */
export function getPublishDateRangeLabel(calendarViewId, date) {
  const anchor = moment(date);
  if (!anchor.isValid()) return "";

  if (calendarViewId === EVENT_SHIFT_VIEW.DAILY) {
    return anchor.format("MMM DD, YYYY");
  }

  if (calendarViewId === EVENT_SHIFT_VIEW.MONTHLY) {
    const start = anchor.clone().startOf("month");
    const end = anchor.clone().endOf("month");
    return `${start.format("MMM DD")} - ${end.format("MMM DD")}`;
  }

  const start = anchor.clone().startOf("week");
  const end = anchor.clone().endOf("week");
  return `${start.format("MMM DD")} - ${end.format("MMM DD")}`;
}

/**
 * Period label and intro copy for the publish confirmation modal.
 * @param {string} calendarViewId
 * @param {Date|string} date
 */
export function getPublishPeriodDetails(calendarViewId, date) {
  const anchor = moment(date);
  if (!anchor.isValid()) {
    return {
      introKey: "scheduler.publishEventShiftIntroMonth",
      periodLabelKey: "scheduler.publishEventShiftPeriodMonth",
      periodValue: "",
    };
  }

  if (calendarViewId === EVENT_SHIFT_VIEW.DAILY) {
    return {
      introKey: "scheduler.publishEventShiftIntroDay",
      periodLabelKey: "scheduler.publishEventShiftPeriodDate",
      periodValue: anchor.format("MMM DD, YYYY"),
    };
  }

  if (calendarViewId === EVENT_SHIFT_VIEW.WEEKLY) {
    const start = anchor.clone().startOf("week");
    const end = anchor.clone().endOf("week");
    return {
      introKey: "scheduler.publishEventShiftIntroWeek",
      periodLabelKey: "scheduler.publishEventShiftPeriodWeek",
      periodValue: `${start.format("MMM DD")} - ${end.format("MMM DD, YYYY")}`,
    };
  }

  return {
    introKey: "scheduler.publishEventShiftIntroMonth",
    periodLabelKey: "scheduler.publishEventShiftPeriodMonth",
    periodValue: anchor.format("MMMM YYYY"),
  };
}

/**
 * @param {{ shiftCount: number, eventCount: number }} counts
 * @param {(key: string, opts?: object) => string} t
 */
export function getPublishItemTypeLabel(counts, t) {
  const { shiftCount, eventCount } = counts;
  if (shiftCount > 0 && eventCount > 0) {
    return t("scheduler.publishItemTypeMixed");
  }
  if (shiftCount > 0) {
    return t("accountOwner.shiftTab").toLowerCase();
  }
  return t("accountOwner.eventTab").toLowerCase();
}
